import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, X, Plus } from "lucide-react";
import { blokkSok, KOLONNER, SPEAKER_STAGES, PARTNER_STAGES, FOKUS, SPOR } from "./blokkData";

const Seg = ({ valg, verdi, onVelg, testidPrefix }) => (
  <div className="dok-blokk-seg">
    {valg.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onVelg(k)} className={verdi === k ? "aktiv" : ""} data-testid={`${testidPrefix}-${k}`}>{l}</button>
    ))}
  </div>
);

export default function PipelineKonfig({ spec, onEndre, onLukk }) {
  const [sok, setSok] = useState("");
  const [treff, setTreff] = useState([]);
  const [navnKart, setNavnKart] = useState({});

  useEffect(() => {
    const t = setTimeout(() => {
      blokkSok(sok, "speakers,partners")
        .then((r) => {
          setTreff(r);
          setNavnKart((prev) => ({ ...prev, ...Object.fromEntries(r.map((x) => [x.id, x])) }));
        })
        .catch(() => setTreff([]));
    }, 200);
    return () => clearTimeout(t);
  }, [sok]);

  const items = spec.items || [];
  const leggTil = (r) => {
    if (items.some((i) => i.id === r.id)) return;
    onEndre({ items: [...items, { kilde: r.kilde, id: r.id, maal: "", fokus: "now", spor: "" }] });
  };
  const fjern = (id) => onEndre({ items: items.filter((i) => i.id !== id) });
  const flytt = (idx, retn) => {
    const m = idx + retn;
    if (m < 0 || m >= items.length) return;
    const ny = [...items];
    [ny[idx], ny[m]] = [ny[m], ny[idx]];
    onEndre({ items: ny });
  };
  const settFelt = (id, felt, verdi) => onEndre({ items: items.map((i) => (i.id === id ? { ...i, [felt]: verdi } : i)) });
  const toggleKol = (k) => {
    const kol = spec.kolonner || [];
    onEndre({ kolonner: kol.includes(k) ? kol.filter((x) => x !== k) : [...kol, k] });
  };
  const stages = spec.kilde === "partners" ? PARTNER_STAGES : SPEAKER_STAGES;

  return (
    <div className="dok-blokk-konfig" contentEditable={false} data-testid="dok-blokk-konfig-panel">
      <div className="flex flex-wrap items-center gap-2">
        <input value={spec.tittel || ""} onChange={(e) => onEndre({ tittel: e.target.value })} placeholder="Blokk-tittel (f.eks. Priority relationships)"
          className="dok-blokk-input flex-1 min-w-[160px]" data-testid="dok-blokk-tittel-input" />
        <Seg valg={[["kuratert", "Kuratert"], ["filter", "Filter"]]} verdi={spec.modus || "kuratert"} onVelg={(m) => onEndre({ modus: m })} testidPrefix="dok-blokk-modus" />
        <Seg valg={[["master", "Master"], ["sprint", "Sprint"], ["tabell", "Tabell"], ["kort", "Kort"]]} verdi={spec.visning || "tabell"} onVelg={(v) => onEndre({ visning: v })} testidPrefix="dok-blokk-visning" />
        <button type="button" onClick={onLukk} className="dok-blokk-knapp" title="Lukk" data-testid="dok-blokk-konfig-lukk"><X size={12} /></button>
      </div>

      {spec.modus === "filter" ? (
        <div className="flex items-center gap-2 mt-2.5">
          <Seg valg={[["speakers", "Speakers"], ["partners", "Partnere"]]} verdi={spec.kilde || "speakers"} onVelg={(k) => onEndre({ kilde: k, filter: { stage: "" } })} testidPrefix="dok-blokk-kilde" />
          <select value={spec.filter?.stage || ""} onChange={(e) => onEndre({ filter: { stage: e.target.value } })}
            className="dok-blokk-input" data-testid="dok-blokk-stage-velger">
            <option value="">Alle aktive (ikke avslått)</option>
            {stages.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      ) : (
        <>
          <input value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Søk etter speakers og partnere …"
            className="dok-blokk-input w-full mt-2.5" data-testid="dok-blokk-sok" />
          {treff.filter((r) => !items.some((i) => i.id === r.id)).length > 0 && (
            <div className="dok-blokk-treff">
              {treff.filter((r) => !items.some((i) => i.id === r.id)).slice(0, 6).map((r) => (
                <button key={r.id} type="button" onClick={() => leggTil(r)} data-testid={`dok-blokk-treff-${r.id}`}>
                  <Plus size={11} />
                  <span className="truncate">{r.navn}</span>
                  <span className="under truncate">{r.under || r.stageLabel}</span>
                </button>
              ))}
            </div>
          )}
          {items.length > 0 && (
            <div className="mt-2 space-y-1">
              {items.map((it, idx) => (
                <div key={it.id} className="dok-blokk-valgt" data-testid={`dok-blokk-valgt-${it.id}`}>
                  <span className="navn truncate">{navnKart[it.id]?.navn || "…"}</span>
                  <input value={it.maal || ""} onChange={(e) => settFelt(it.id, "maal", e.target.value)} placeholder="Mål i planen …"
                    className="dok-blokk-input flex-1 min-w-0" data-testid={`dok-blokk-maal-${it.id}`} />
                  <select value={it.fokus || "now"} onChange={(e) => settFelt(it.id, "fokus", e.target.value)}
                    className="dok-blokk-input" data-testid={`dok-blokk-fokus-${it.id}`}>
                    {FOKUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <select value={it.spor || ""} onChange={(e) => settFelt(it.id, "spor", e.target.value)}
                    className="dok-blokk-input" data-testid={`dok-blokk-spor-${it.id}`}>
                    <option value="">Uten spor</option>
                    {SPOR.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button type="button" onClick={() => flytt(idx, -1)} disabled={idx === 0} title="Opp" data-testid={`dok-blokk-opp-${it.id}`}><ArrowUp size={11} /></button>
                  <button type="button" onClick={() => flytt(idx, 1)} disabled={idx === items.length - 1} title="Ned" data-testid={`dok-blokk-ned-${it.id}`}><ArrowDown size={11} /></button>
                  <button type="button" onClick={() => fjern(it.id)} className="slett" title="Fjern" data-testid={`dok-blokk-fjern-${it.id}`}><X size={11} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {spec.visning !== "sprint" && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2.5">
          <span className="dok-blokk-type">Kolonner</span>
          {KOLONNER.map(([k, l]) => (
            <label key={k} className="dok-blokk-kol">
              <input type="checkbox" checked={(spec.kolonner || []).includes(k)} onChange={() => toggleKol(k)} data-testid={`dok-blokk-kol-${k}`} />
              {l}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
